/**
 * Custom hook for managing comment data
 * Responsibility: Data fetching and transformation
 */

import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import {
  realtorCommentsService,
  CommentItem,
} from "@/services/realtor-comments.service";
import { createLogger } from "@/utils/logger";

import { ICommentItem } from "../PropertyComments";

const logger = createLogger("PropertyComments:useCommentData");

// Role mapping and user enrichment is now handled by backend API
// API returns fully enriched comments with user data from users collection

export interface UseCommentDataProps {
  /** Property ID to fetch comments for */
  propertyId: string;
  /** Optional pre-loaded comments */
  comments?: ICommentItem[];
}

export const useCommentData = ({
  propertyId,
  comments,
}: UseCommentDataProps) => {
  const t = useTranslations();

  // Fetch comments from API if not provided
  const {
    data,
    isLoading: isListLoading,
    error: listError,
  } = useQuery({
    queryKey: ["property-comments", "list", { propertyId }],
    queryFn: async () => {
      return realtorCommentsService.list(propertyId);
    },
    enabled: true, // Always load fresh data from API
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // Don't retry on 401 - authentication required
      if (error?.status === 401) return false;
      return failureCount < 2;
    },
  });

  // Log API errors for debugging
  React.useEffect(() => {
    if (listError) {
      logger.error("Comments list error:", listError);
    }
  }, [listError]);

  /**
   * Build a unified list of comments for display with replies support
   * Works with new normalized schema - user data is pre-enriched by service
   */
  const displayComments = React.useMemo(() => {
    // FIX: Prioritize API data over props to get fresh data
    // Check API data first, then fallback to props

    // Extract comments from various possible response structures
    let apiList: CommentItem[] = [];
    if (data?.comments) {
      // Direct response from our API: { comments: [...], pagination: {...} }
      apiList = data.comments as CommentItem[];
    } else if (data?.data?.comments) {
      // Nested response structure: { data: { comments: [...] } }
      apiList = data.data.comments as CommentItem[];
    } else if (Array.isArray(data?.data)) {
      // Array response: { data: [...] }
      apiList = data.data as CommentItem[];
    } else if (Array.isArray(data)) {
      // Direct array: [...]
      apiList = data as CommentItem[];
    }

    const mapComment = (comment: any): ICommentItem => {
      // Ensure user object exists and has required properties
      const user = comment.user || {
        displayName: "Unknown User",
        primaryRole: "user",
        avatar: null,
        location: null,
      };

      // Map enriched API data to component interface
      const mappedComment: ICommentItem = {
        ...comment,
        user, // Ensure user object is always present
        // Legacy mappings for backward compatibility
        message: comment.content,
        timestamp: comment.createdAt,
        userId: user, // Map user object to userId for legacy component code
        replies: comment.replies ? comment.replies.map(mapComment) : [],
      };

      return mappedComment;
    };

    // If no API data available, use props as fallback
    if (apiList.length === 0 && comments && comments.length > 0) {
      if (process.env.NODE_ENV === "development") {
        logger.debug("Using fallback to props comments");
      }
      return comments;
    }

    if (process.env.NODE_ENV === "development") {
      logger.debug("Using API data, apiList.length:", apiList.length);
    }
    return apiList.map(mapComment);
  }, [comments, data]);

  return {
    displayComments,
    isListLoading,
    listError,
    data,
  };
};
